import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Save, FileText } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const JobType = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  const [jobTypes,         setJobTypes]         = useState([]);
  const [jobModes,         setJobModes]         = useState([]);
  const [revenueTypes,     setRevenueTypes]     = useState([]);
  const [filteredJobTypes, setFilteredJobTypes] = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [searchTerm,       setSearchTerm]       = useState('');
  const [showModal,        setShowModal]        = useState(false);
  const [editMode,         setEditMode]         = useState(false);
  const [submitLoading,    setSubmitLoading]    = useState(false);
  const [errors,           setErrors]           = useState({});

  const [formData, setFormData] = useState({
    JobTypeID: null, JobDescription: '', JobModeID: '',
    JobCode: '', JobPrefix: '', StatusSea: 1, Remarks: '',
  });

  const emptyRevRow = () => ({ id: Date.now(), RevenueTypeID: '', Amount: 0, Cost: 0, RevenueType: '', RevenueCode: '' });
  const [revenueItems, setRevenueItems] = useState([emptyRevRow()]);

  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchJobModes(); fetchRevenueTypes(); fetchJobTypes();
  }, [companyId, yearId]);

  useEffect(() => { filterJobTypes(); }, [searchTerm, jobTypes]);

  const fetchJobModes = async () => {
    try {
      const res = await apiClient.get('/JobMode.php');
      if (res.data.success) setJobModes(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchRevenueTypes = async () => {
    try {
      const res = await apiClient.get('/RevenueType.php');
      if (res.data.success) setRevenueTypes(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchJobTypes = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/JobType.php');
      if (res.data.success) setJobTypes(res.data.data || []);
      else alert('Failed to fetch job types: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching job types.');
    } finally { setLoading(false); }
  };

  const filterJobTypes = () => {
    if (!searchTerm.trim()) { setFilteredJobTypes(jobTypes); return; }
    const q = searchTerm.toLowerCase();
    setFilteredJobTypes(jobTypes.filter(jt =>
      jt.JobDescription.toLowerCase().includes(q) || jt.JobCode?.toLowerCase().includes(q)
    ));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.JobDescription.trim()) errs.JobDescription = 'Job description is required';
    if (!formData.JobModeID) errs.JobModeID = 'Job mode is required';
    if (!formData.JobCode?.trim()) errs.JobCode = 'Job code is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const validRevenues = revenueItems.filter(r => r.RevenueTypeID);
      const payload = {
        ...formData, StatusSea: formData.StatusSea ? 1 : 0,
        revenues: validRevenues.map(r => ({ RevenueTypeID: r.RevenueTypeID, Amount: parseFloat(r.Amount) || 0, Cost: parseFloat(r.Cost) || 0 })),
      };
      const res = await apiClient[editMode ? 'put' : 'post']('/JobType.php', payload);
      if (res.data.success) {
        alert(editMode ? 'Job type updated successfully!' : 'Job type created successfully!');
        closeModal(); fetchJobTypes();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving job type: ' + (err.response?.data?.message || err.message));
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = async (jobType) => {
    try {
      const res = await apiClient.get(`/JobType.php?id=${jobType.JobTypeID}`);
      if (res.data.success) {
        const data = res.data.data;
        setFormData({ JobTypeID: data.JobTypeID, JobDescription: data.JobDescription, JobModeID: data.JobModeID, JobCode: data.JobCode, JobPrefix: data.JobPrefix || '', StatusSea: data.StatusSea === 1, Remarks: data.Remarks || '' });
        if (data.revenues?.length > 0) {
          const mapped = data.revenues.map((r, i) => ({ id: Date.now() + i, RevenueTypeID: r.RevenueTypeID, Amount: parseFloat(r.Amount) || 0, Cost: parseFloat(r.Cost) || 0, RevenueType: r.RevenueType, RevenueCode: r.RevenueCode }));
          mapped.push({ id: Date.now() + data.revenues.length, RevenueTypeID: '', Amount: 0, Cost: 0, RevenueType: '', RevenueCode: '' });
          setRevenueItems(mapped);
        } else { setRevenueItems([emptyRevRow()]); }
        setEditMode(true); setShowModal(true); setErrors({});
      }
    } catch { alert('Error loading job type details'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job type?')) return;
    try {
      const res = await apiClient.delete('/JobType.php', { data: { JobTypeID: id } });
      if (res.data.success) { alert('Job type deleted successfully!'); fetchJobTypes(); }
      else alert('Error: ' + res.data.message);
    } catch { alert('Error deleting job type'); }
  };

  const resetForm = () => {
    setFormData({ JobTypeID: null, JobDescription: '', JobModeID: '', JobCode: '', JobPrefix: '', StatusSea: 1, Remarks: '' });
    setRevenueItems([emptyRevRow()]); setEditMode(false); setErrors({});
  };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const handleRevenueTypeChange = (index, revenueTypeId) => {
    const selected = revenueTypes.find(rt => rt.RevenueTypeID === parseInt(revenueTypeId));
    const next = [...revenueItems];
    next[index] = { ...next[index], RevenueTypeID: revenueTypeId, Amount: parseFloat(selected?.MinimumValue) || 0, Cost: parseFloat(selected?.RevenueCost) || 0, RevenueType: selected?.RevenueType || '', RevenueCode: selected?.RevenueCode || '' };
    setRevenueItems(next);
    if (index === revenueItems.length - 1 && revenueTypeId) addRevenueRow();
  };

  const handleRevenueFieldChange = (index, field, value) => {
    const next = [...revenueItems]; next[index][field] = value; setRevenueItems(next);
  };

  const addRevenueRow  = () => setRevenueItems(p => [...p, emptyRevRow()]);
  const deleteRevenueRow = (index) => { if (revenueItems.length > 1) setRevenueItems(p => p.filter((_, i) => i !== index)); };

  const inputCls  = (err) => `w-full px-3.5 py-2.5 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = (err) => `w-full px-3.5 py-2.5 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const labelCls  = 'block mb-1.5 text-xs font-bold text-gray-500 uppercase tracking-wide';
  const errMsg    = (msg) => msg ? <p className="text-xs text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  const SectionLabel = ({ icon: Icon, title }) => (
    <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wide
      px-3.5 py-2.5 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg mb-4"
      style={{ borderLeftColor: NAVY }}>
      {Icon && <Icon size={13} />} {title}
    </div>
  );

  // ── no session ─────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <FileText size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <FileText size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Job Type Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage job types and revenue structures</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Job Type
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by description or code..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredJobTypes.length} {filteredJobTypes.length === 1 ? 'job type' : 'job types'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading job types...</div>
        ) : filteredJobTypes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <FileText size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No job types found</p>
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear search</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Job Code', 'Description', 'Job Mode', 'Type', 'Prefix', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredJobTypes.map(jt => (
                  <tr key={jt.JobTypeID}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
                        {jt.JobCode}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">{jt.JobDescription}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">{jt.JobMode || 'N/A'}</td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border-2 ${
                        jt.StatusSea
                          ? 'bg-blue-50 text-blue-700 border-blue-300'
                          : 'bg-sky-50 text-sky-700 border-sky-300'
                      }`}>
                        {jt.StatusSea ? '🌊 Sea' : '✈️ Air'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">{jt.JobPrefix || '-'}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(jt)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(jt.JobTypeID)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[820px] shadow-2xl border border-gray-200 overflow-hidden max-h-[92vh] flex flex-col"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <FileText size={18} /> {editMode ? 'Edit Job Type' : 'Add New Job Type'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-7">

              {/* ── Basic Info ── */}
              <SectionLabel icon={FileText} title="Basic Information" />

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Job Code <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.JobCode}
                    onChange={e => setFormData({ ...formData, JobCode: e.target.value.toUpperCase() })}
                    maxLength="20" placeholder="e.g., IMP-SEA" className={inputCls(errors.JobCode)} />
                  {errMsg(errors.JobCode)}
                </div>
                <div>
                  <label className={labelCls}>Job Prefix</label>
                  <input type="text" value={formData.JobPrefix}
                    onChange={e => setFormData({ ...formData, JobPrefix: e.target.value })}
                    maxLength="10" placeholder="e.g., JOB" className={inputCls(false)} />
                </div>
              </div>

              <div className="mb-4">
                <label className={labelCls}>Job Description <span className="text-red-500">*</span></label>
                <input type="text" value={formData.JobDescription}
                  onChange={e => setFormData({ ...formData, JobDescription: e.target.value })}
                  maxLength="100" placeholder="e.g., Import Sea Freight" className={inputCls(errors.JobDescription)} />
                {errMsg(errors.JobDescription)}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Job Mode <span className="text-red-500">*</span></label>
                  <select value={formData.JobModeID} onChange={e => setFormData({ ...formData, JobModeID: e.target.value })}
                    className={selectCls(errors.JobModeID)}>
                    <option value="">-- Select Job Mode --</option>
                    {jobModes.map(m => <option key={m.JobModeID} value={m.JobModeID}>{m.JobMode}</option>)}
                  </select>
                  {errMsg(errors.JobModeID)}
                </div>
                <div>
                  <label className={labelCls}>Transport Type</label>
                  <div className="flex gap-4 pt-3">
                    {[{ val: 1, label: '🌊 Sea' }, { val: 0, label: '✈️ Air' }].map(({ val, label }) => (
                      <label key={val} className="flex items-center gap-2.5 cursor-pointer px-4 py-2.5 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150 select-none">
                        <input type="radio" name="StatusSea"
                          checked={formData.StatusSea === val || formData.StatusSea === (val === 1)}
                          onChange={() => setFormData({ ...formData, StatusSea: val })}
                          className="w-4 h-4 accent-[#0d1c6b]" />
                        <span className="text-sm font-semibold text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className={labelCls}>Remarks</label>
                <textarea value={formData.Remarks} onChange={e => setFormData({ ...formData, Remarks: e.target.value })}
                  rows={3} placeholder="Additional notes..."
                  className="w-full px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] resize-y font-[inherit]" />
              </div>

              {/* ── Revenue Grid ── */}
              <SectionLabel title="Revenue Structure" />
              <div className="overflow-x-auto mb-3">
                <table className="w-full border-collapse min-w-[560px]">
                  <thead>
                    <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                      {['Revenue Type', 'Amount', 'Cost', ''].map((h, i) => (
                        <th key={i} className={`px-3 py-3 text-[11px] font-bold uppercase tracking-wider ${i > 0 ? 'text-right' : 'text-left'} ${i === 3 ? 'w-12' : ''}`}
                          style={{ color: '#fff' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {revenueItems.map((item, index) => (
                      <tr key={item.id} className="border-b border-gray-100">
                        <td className="px-3 py-2">
                          <select value={item.RevenueTypeID} onChange={e => handleRevenueTypeChange(index, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 bg-white outline-none focus:border-[#0d1c6b]">
                            <option value="">-- Select Revenue Type --</option>
                            {revenueTypes.map(rt => (
                              <option key={rt.RevenueTypeID} value={rt.RevenueTypeID}>{rt.RevenueType} ({rt.RevenueCode})</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" step="0.01" value={item.Amount}
                            onChange={e => handleRevenueFieldChange(index, 'Amount', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 bg-white outline-none focus:border-[#0d1c6b] text-right" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" step="0.01" value={item.Cost}
                            onChange={e => handleRevenueFieldChange(index, 'Cost', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 bg-white outline-none focus:border-[#0d1c6b] text-right" />
                        </td>
                        <td className="px-3 py-2 text-center">
                          {revenueItems.length > 1 && (
                            <button type="button" onClick={() => deleteRevenueRow(index)} title="Delete row"
                              className="w-7 h-7 flex items-center justify-center rounded-md border-0 cursor-pointer bg-red-100 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white">
                              <Trash2 size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button type="button" onClick={addRevenueRow}
                className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50 hover:border-gray-300 mb-6">
                <Plus size={15} /> Add Revenue Item
              </button>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  <Save size={16} /> {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default JobType;